<template>
  <div class="fuel-consumption-trend card">
    <div class="fuel-consumption-trend__header">
      <div class="fuel-consumption-trend__heading">
        <h5 class="fuel-consumption-trend__title">
          {{
            filterType == 'date'
              ? $t('dashboard.title.fuelConsumptionDailyTrend')
              : $t('dashboard.title.fuelConsumptionVehicleTrend')
          }}
        </h5>
        <span class="fuel-consumption-trend__subtitle">{{ getFilterLabel }}</span>
      </div>
      <div class="fuel-consumption-trend__controls">
        <div class="fuel-consumption-trend__select" v-if="filterType == 'date'">
          <multiselect
            :key="groupingOptionKey"
            :value="selectedGrouping"
            :options="groupingOption"
            track-by="key"
            label="label"
            :allow-empty="false"
            :searchable="false"
            :show-labels="false"
            @input="changeGrouping"
          />
        </div>
        <div class="fuel-consumption-trend__toggle">
          <b-form-radio-group
            v-model="filterType"
            :options="filterTypeOptions"
            name="fuel-consumption-trend-filter"
            buttons
            button-variant="outline-primary"
            class="mc-btn--toggle"
            :disabled="dataChart.line.isLoading || dataChart.bar.isLoading"
          />
        </div>
      </div>
    </div>
    <div v-if="filterType == 'date'" class="fuel-consumption-trend__summary mb-3">
      <span v-for="sum in getAvg" :key="sum.key" class="fuel-consumption-trend__summary-item">
        {{ $t(sum.title) }}: {{ sum.formatting(sum.value) }}
      </span>
    </div>
    <div v-else class="fuel-consumption-trend__thresholds mb-3">
      <div
        v-for="(sum, index) in getAvg"
        :key="`v-${sum.key}`"
        class="fuel-consumption-trend__threshold"
      >
        <div class="fuel-consumption-trend__threshold-heading">
          <i :class="`mc-fa ${sum.icon}`" :style="{ color: sum.iconColor }"></i>
          <span>{{ $t(sum.title2) }}</span>
        </div>
        <div class="fuel-consumption-trend__threshold-control">
          <span class="fuel-consumption-trend__threshold-label">
            {{ $t('dashboard.label.avg') }}
          </span>
          <FormulateInput
            type="number"
            suffix
            validation="number"
            validation-behavior="live"
            error-behavior="live"
            v-model="sum.value"
            @input="changeValueAvg(index)"
            class="fuel-consumption-trend__threshold-input"
          >
            <template #suffix>
              <div class="formulate-input-suffix">
                <span>{{ sum.format }}</span>
              </div>
            </template>
          </FormulateInput>
        </div>
      </div>
    </div>
    <b-skeleton
      v-if="dataChart.line.isLoading || dataChart.bar.isLoading"
      width="100%"
      height="380px"
    />
    <LineReport
      :key="lineChartKey"
      v-if="!dataChart.line.isLoading && filterType == 'date'"
      :labels="dataChart.line.labels ?? []"
      :datasets="dataChart.line.datasets ?? []"
      :y-axes-additional="dataChart.line.yAxesAdditional"
      :options="dataChart.line.options"
      point-label
    />
    <div
      v-else-if="!dataChart.bar.isLoading && filterType == 'vehicle'"
      class="fuel-consumption-trend__vehicle-chart"
    >
      <div v-if="hasVehicleChartData" class="fuel-consumption-trend__chart-viewport">
        <b-row class="fuel-consumption-trend__chart-row">
          <b-col v-for="(chart, index) in vehicleBarCharts" :key="chart.key" md="2">
            <div class="fuel-consumption-trend__chart" :style="barChartStyle">
              <HorizontalBar
                :key="fuelSummaryTrend[index].key"
                :labels="dataChart.bar.labels ?? []"
                :datasets="chart.datasets ?? []"
                :options="chart.options"
                :height="barChartHeight"
                :styles="barChartStyle"
                :bar-label="true"
                :unit-label="chart.datasets[0].unit"
              />
            </div>
          </b-col>
        </b-row>
      </div>
      <div v-else class="fuel-consumption-trend__empty">
        {{ $t('global.text.dataNotFound') }}
      </div>
    </div>
    <FuelConsumptionTrendChartDetail :modalId="modalId" ref="fuelConsumptionTrendDetail" />
  </div>
</template>
<script>
import LineReport from '@/components/Chart/LineReport.vue'
import { Helper } from '@/mixin/helper'
import { QueryCache } from '@/mixin/query-cache'
import { SUMMARY_FUEL_DASHBOARD } from '@/services/api/dashboard/dashboard'
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import HorizontalBar from '@/components/Chart/HorizontalBar.vue'
import { debounce } from '@/helpers/debounce'
import FuelConsumptionTrendChartDetail from './FuelConsumptionTrendChartDetail.vue'
import { mapGetters } from 'vuex'

moment.locale('id')

const BAR_CHART_MIN_HEIGHT = 370
const BAR_CHART_ROW_HEIGHT = 28
const BAR_CHART_VERTICAL_PADDING = 20

export default {
  components: { Multiselect, LineReport, HorizontalBar, FuelConsumptionTrendChartDetail },
  mixins: [Helper],
  data() {
    return {
      modalId: 'modal-fuel-dashborad-detail',
      lineChartKey: 0,
      filterLabel: '',
      grouping: 'd',
      filterType: 'date',
      fuelDashboardData: [],
      vgroupId: [],
      vehicleTypeId: [],
      period: [],
      queryCache: new QueryCache(),
      dataChart: {
        line: {
          isLoading: false,
          labels: [],
          datasets: [
            {
              label: this.$t('dashboard.label.fuelConsumption'),
              unit: 'L',
              data: [],
              borderColor: '#77ACE9',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
              hidden: true,
            },
            {
              label: this.$t('dashboard.label.efficiency'),
              unit: 'Km/L',
              data: [],
              borderColor: '#137C55',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
            {
              label: this.$t('dashboard.label.trip'),
              unit: 'Km',
              data: [],
              borderColor: '#1C74DA',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
              hidden: true,
            },
            {
              label: this.$t('dashboard.label.driverAlert'),
              unit: '',
              data: [],
              borderColor: '#F41D00',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
              hidden: true,
            },
            {
              label: this.$t('dashboard.label.idleDuration'),
              unit: 'min',
              data: [],
              borderColor: '#FF8A00',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
              hidden: true,
            },
            {
              label: this.$t('dashboard.label.tripDuration'),
              unit: 'min',
              data: [],
              borderColor: '#CC1D92',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
              hidden: true,
            },
          ],
          yAxesAdditional: {
            display: false,
          },
          options: {
            layout: {
              padding: {
                top: 24,
                right: 64,
              },
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    callback: (value) => {
                      if (Number.isInteger(value)) {
                        return value
                      }
                    },
                  },
                },
              ],
            },
          },
        },
        bar: {
          isLoading: false,
          labels: [],
          datasetFuel: [
            {
              label: this.$t('dashboard.label.fuelConsumption'),
              type: 'bar',
              data: [],
              fill: true,
              unit: 'L',
              spanGaps: false,
              backgroundColor: '#77ACE9',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.fuelConsumption'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetEfficiency: [
            {
              label: this.$t('dashboard.label.efficiency'),
              type: 'bar',
              data: [],
              fill: true,
              unit: 'Km/L',
              spanGaps: false,
              backgroundColor: '#137C55',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.efficiency'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetTrip: [
            {
              label: this.$t('dashboard.label.trip'),
              type: 'bar',
              data: [],
              fill: true,
              unit: 'Km',
              spanGaps: false,
              backgroundColor: '#1C74DA',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.trip'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetDriverAlert: [
            {
              label: this.$t('dashboard.label.driverAlert'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: '',
              backgroundColor: '#F41D00',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.driverAlert'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetIdleDuration: [
            {
              label: this.$t('dashboard.label.idleDuration'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'min',
              backgroundColor: '#FF8A00',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.idleDuration'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetTripDuration: [
            {
              label: this.$t('dashboard.label.tripDuration'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'min',
              backgroundColor: '#CC1D92',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.tripDuration'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
        },
      },
      fuelSummaryTrend: [
        {
          key: 'avgFuelConsumption',
          title: 'dashboard.label.avgFuelConsumption',
          title2: 'dashboard.label.fuelConsumption',
          icon: 'icon-outline_fuel',
          value: 0,
          format: 'L',
          iconColor: '#77ACE9',
          key: 0,
          formatting: (value) => {
            return value != null ? `${value} L` : 'N/A'
          },
        },
        {
          key: 'avgEfficiency',
          title: 'dashboard.label.avgEfficiency',
          title2: 'dashboard.label.efficiency',
          icon: 'icon-fill_droplet',
          value: 0,
          format: 'Km/L',
          iconColor: '#137C55',
          key: 10,
          formatting: (value) => {
            return value != null ? `${value} Km/L` : 'N/A'
          },
        },
        {
          key: 'avgTrip',
          title: 'dashboard.label.avgTrip',
          title2: 'dashboard.label.trip',
          icon: 'icon-outline_calculate',
          value: 0,
          format: 'Km',
          iconColor: '#1C74DA',
          key: 20,
          formatting: (value) => {
            return value != null ? `${value} Km` : 'N/A'
          },
        },
        {
          key: 'avgAlert',
          title: 'dashboard.label.avgAlert',
          title2: 'dashboard.label.driverAlert',
          icon: 'icon-fill_alert-triangle',
          value: 0,
          format: this.$t('dashboard.label.incident'),
          iconColor: '#F41D00',
          key: 30,
          formatting: (value) => {
            return value != null ? `${value}` : 'N/A'
          },
        },
        {
          key: 'avgIdleDuration',
          title: 'dashboard.label.avgIdleDuration',
          title2: 'dashboard.label.idleDuration',
          icon: 'icon-outline_truck-idle',
          value: 0,
          format: 'min',
          iconColor: '#FF8A00',
          key: 40,
          formatting: (value) => {
            return value != null ? this.convertTimeToString(value) : 'N/A'
          },
        },
        {
          key: 'avgTripDuration',
          title: 'dashboard.label.avgTripDuration',
          title2: 'dashboard.label.tripDuration',
          icon: 'icon-fill_timer',
          value: 0,
          format: 'min',
          iconColor: '#CC1D92',
          key: 50,
          formatting: (value) => {
            return value != null ? this.convertTimeToString(value) : 'N/A'
          },
        },
      ],
      isFirstInit: true,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    barChartOptions() {
      return {
        onClick: (event, activeElements) => {
          if (activeElements.length <= 0) return
          const index = activeElements[0]._index
          this.redirectToDetail(index)
        },
        legend: {
          display: false,
        },
        layout: {
          padding: {
            top: 2,
            right: 22,
            bottom: 0,
            left: 0,
          },
        },
        plugins: {
          datalabels: {
            align: 'right',
            anchor: 'end',
            clamp: true,
            color: '#516078',
            font: {
              size: 11,
              weight: 'normal',
            },
          },
        },
        scales: {
          xAxes: [
            {
              afterDataLimits(scale) {
                if (scale.max > 0) {
                  scale.max *= 1.5
                }
              },
              ticks: {
                beginAtZero: true,
                display: false,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                borderDash: [5, 5],
                color: '#e3e8f0',
                drawBorder: false,
                zeroLineColor: '#e3e8f0',
              },
              ticks: {
                fontColor: '#4f5664',
                fontSize: 11,
                padding: 4,
                callback(value) {
                  if (!value || value.length <= 10) return value
                  const partLength = Math.floor((10 - 3) / 2)
                  return value.slice(0, partLength) + '...' + value.slice(-partLength)
                },
              },
            },
          ],
        },
      }
    },
    barChartOptionsWithoutYLabel() {
      return {
        onClick: (event, activeElements) => {
          if (activeElements.length <= 0) return
          const index = activeElements[0]._index
          this.redirectToDetail(index)
        },
        legend: {
          display: false,
        },
        layout: {
          padding: {
            top: 2,
            right: 22,
            bottom: 0,
            left: 0,
          },
        },
        plugins: {
          datalabels: {
            align: 'right',
            anchor: 'end',
            clamp: true,
            color: '#516078',
            font: {
              size: 11,
              weight: 'normal',
            },
          },
        },
        scales: {
          xAxes: [
            {
              afterDataLimits(scale) {
                if (scale.max > 0) {
                  scale.max *= 1.5
                }
              },
              ticks: {
                beginAtZero: true,
                display: false,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                borderDash: [5, 5],
                color: '#e3e8f0',
                drawBorder: false,
                zeroLineColor: '#e3e8f0',
              },
              ticks: {
                display: false,
              },
            },
          ],
        },
      }
    },
    getFilterLabel() {
      return this.filterLabel
    },
    getAvg() {
      return this.fuelSummaryTrend
    },
    barChartHeight() {
      const vehicleCount = this.dataChart.bar.labels.length
      const contentHeight = vehicleCount * BAR_CHART_ROW_HEIGHT + BAR_CHART_VERTICAL_PADDING

      return Math.max(BAR_CHART_MIN_HEIGHT, contentHeight)
    },
    barChartStyle() {
      return {
        position: 'relative',
        height: `${this.barChartHeight}px`,
      }
    },
    hasVehicleChartData() {
      return this.dataChart.bar.labels.length > 0
    },
    vehicleBarCharts() {
      return [
        {
          key: 'fuel',
          datasets: this.dataChart.bar.datasetFuel,
          options: this.barChartOptions,
        },
        {
          key: 'efficiency',
          datasets: this.dataChart.bar.datasetEfficiency,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'trip',
          datasets: this.dataChart.bar.datasetTrip,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'driver-alert',
          datasets: this.dataChart.bar.datasetDriverAlert,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'idle-duration',
          datasets: this.dataChart.bar.datasetIdleDuration,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'trip-duration',
          datasets: this.dataChart.bar.datasetTripDuration,
          options: this.barChartOptionsWithoutYLabel,
        },
      ]
    },
    selectedGrouping() {
      return this.groupingOption.find((option) => option.key === this.grouping)
    },
    groupingOption() {
      const options = [
        {
          key: 'd',
          label: this.$t('dashboard.text.costPeriod.last7Days'),
        },
      ]
      const diff = moment(this.period[1]).diff(moment(this.period[0]), 'day')
      if (diff >= 6) {
        options.push({
          key: 'w',
          label: this.$t('dashboard.text.costPeriod.last7Weeks'),
        })
      }
      return options
    },
    groupingOptionKey() {
      return this.groupingOption.map((option) => option.key).join('-')
    },
    filterTypeOptions() {
      return [
        {
          html: `<i class="icon-outline_calendar"></i><span>${this.$t(
            'global.object.period'
          )}</span>`,
          value: 'date',
        },
        {
          html: `<i class="icon-outline_car-group"></i><span>${this.$t('global.fleet')}</span>`,
          value: 'vehicle',
        },
      ]
    },
  },
  watch: {
    filterType() {
      this.loadData({
        vgroupId: this.vgroupId,
        vehicleTypeId: this.vehicleTypeId,
        startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
        groupBy: this.filterType,
      })
    },
  },
  methods: {
    redirectToDetail(index) {
      const vehicleId = this.fuelDashboardData[index].vehicle?.id ?? 0
      if (!vehicleId) return
      const licensePlate = this.fuelDashboardData[index].vehicle?.licensePlate ?? '-'
      const filter = {
        vehicleId: [vehicleId],
        startDate: this.period[0],
        endDate: this.period[1],
        filterLabel: this.getFilterLabel,
        licensePlate,
      }
      this.$refs.fuelConsumptionTrendDetail.loadFilter(filter)
      this.$bvModal.show(this.modalId)
    },
    changeValueAvg(index) {
      if (!this.isFirstInit) {
        this.setAvgVehicleChartByIndex(index)
      }
    },
    changeGrouping(grouping) {
      this.grouping = grouping.key
      this.mapFuelDashboardToChart(this.fuelDashboardData)
      this.lineChartKey += 1
    },
    loadFilter(filter) {
      this.vgroupId = filter.vgroupId
      this.vehicleTypeId = filter.vehicleTypeId
      const shouldRefreshGrouping =
        !this.period || filter.startDate != this.period[0] || filter.endDate != this.period[1]
      this.period = [filter.startDate, filter.endDate]
      if (shouldRefreshGrouping) {
        this.resetGrouping()
      }
      this.updateFilterLabel()
      this.loadData({
        vgroupId: this.vgroupId,
        vehicleTypeId: this.vehicleTypeId,
        startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
        groupBy: this.filterType,
      })
    },
    async loadData(data) {
      this.isFirstInit = true
      let filters = { ...data }
      let filter = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupBy: filters.groupBy || this.filterType,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }
      if (filters.vgroupId && filters.vgroupId.length > 0) {
        filter = {
          ...filter,
          vgroupId: filters.vgroupId,
        }
      }
      if (filters.vehicleTypeId && filters.vehicleTypeId.length > 0) {
        filter = {
          ...filter,
          vehicleTypeId: filters.vehicleTypeId,
        }
      }

      this.dataChart.line.isLoading = true
      this.dataChart.bar.isLoading = true

      try {
        const result = await this.queryCache.fetch(
          SUMMARY_FUEL_DASHBOARD,
          {
            filter,
          },
          ''
        )
        const fuel = result.summaryFuelDashboard.summaryFuelDashboard.data || []
        this.fuelDashboardData = fuel

        this.mapFuelDashboardToChart(this.fuelDashboardData)
        this.mapAverageFuelData(this.fuelDashboardData, filters.groupBy)
      } finally {
        this.dataChart.line.isLoading = false
        this.dataChart.bar.isLoading = false
      }
    },
    resetGrouping() {
      if (!this.groupingOption.some((option) => option.key === this.grouping)) {
        this.grouping = 'd'
      }
    },
    mapFuelDashboardToChart(fuel) {
      if (this.filterType == 'date') {
        const groupedFuel = this.grouping == 'w' ? this.groupFuelDashboardByDate(fuel) : fuel
        this.mapFuelDashboardDateGrouping(groupedFuel)
      } else {
        this.mapFuelDashboardVehicleGrouping(fuel)
      }
    },
    mapAverageFuelData(fuel, groupBy) {
      const totalData = {
        avgFuelConsumption: 0,
        avgEfficiency: 0,
        avgTrip: 0,
        avgAlert: 0,
        avgIdleDuration: 0,
        avgTripDuration: 0,
      }
      for (const f of fuel) {
        totalData.avgFuelConsumption += f.fuelConsumption
        totalData.avgTrip += f.distanceInKm
        totalData.avgAlert += f.driverAlert
        totalData.avgIdleDuration += f.idleDurationInSecond
        totalData.avgTripDuration += f.tripDurationInSecond
      }
      if (fuel.length) {
        totalData.avgFuelConsumption = Number(
          (totalData.avgFuelConsumption / fuel.length).toFixed(2)
        )
        totalData.avgTrip = Number((totalData.avgTrip / fuel.length).toFixed(2))
        totalData.avgAlert = Number((totalData.avgAlert / fuel.length).toFixed(0))
        totalData.avgIdleDuration = Number((totalData.avgIdleDuration / fuel.length).toFixed(0))
        totalData.avgTripDuration = Number((totalData.avgTripDuration / fuel.length).toFixed(0))
        totalData.avgEfficiency = totalData.avgFuelConsumption
          ? Number((totalData.avgTrip / totalData.avgFuelConsumption).toFixed(2))
          : null
        if (groupBy == 'date') {
          this.fuelSummaryTrend[0].value = totalData.avgFuelConsumption
          this.fuelSummaryTrend[1].value = totalData.avgEfficiency
          this.fuelSummaryTrend[2].value = totalData.avgTrip
          this.fuelSummaryTrend[3].value = totalData.avgAlert
          this.fuelSummaryTrend[4].value = totalData.avgIdleDuration
          this.fuelSummaryTrend[5].value = totalData.avgTripDuration
        } else {
          this.fuelSummaryTrend[0].value = Number(totalData.avgFuelConsumption.toFixed(0))
          this.fuelSummaryTrend[1].value =
            totalData.avgEfficiency == null ? null : Number(totalData.avgEfficiency.toFixed(0))
          this.fuelSummaryTrend[2].value = Number(totalData.avgTrip.toFixed(0))
          this.fuelSummaryTrend[3].value = totalData.avgAlert
          this.fuelSummaryTrend[4].value = Number((totalData.avgIdleDuration / 60).toFixed(0))
          this.fuelSummaryTrend[5].value = Number((totalData.avgTripDuration / 60).toFixed(0))
        }
      } else {
        this.fuelSummaryTrend[0].value = null
        this.fuelSummaryTrend[1].value = null
        this.fuelSummaryTrend[2].value = null
        this.fuelSummaryTrend[3].value = null
        this.fuelSummaryTrend[4].value = null
        this.fuelSummaryTrend[5].value = null
      }

      if (groupBy == 'vehicle') {
        this.setAvgVehicleChart()
      }
    },
    setAvgVehicleChart() {
      this.dataChart.bar.datasetFuel[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[0].value)
      )
      this.dataChart.bar.datasetEfficiency[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[1].value)
      )
      this.dataChart.bar.datasetTrip[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[2].value)
      )
      this.dataChart.bar.datasetDriverAlert[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[3].value)
      )
      this.dataChart.bar.datasetIdleDuration[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[4].value)
      )
      this.dataChart.bar.datasetTripDuration[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[5].value)
      )
      this.isFirstInit = false
    },
    setAvgVehicleChartByIndex: debounce(function (index) {
      switch (index) {
        case 0: {
          this.dataChart.bar.datasetFuel[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
        case 1: {
          this.dataChart.bar.datasetEfficiency[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
        case 2: {
          this.dataChart.bar.datasetTrip[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
        case 3: {
          this.dataChart.bar.datasetDriverAlert[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
        case 4: {
          this.dataChart.bar.datasetIdleDuration[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
        case 5: {
          this.dataChart.bar.datasetTripDuration[1].data = this.fuelDashboardData.map((item) =>
            Number(this.fuelSummaryTrend[index].value)
          )
          break
        }
      }
      this.fuelSummaryTrend[index].key += 1
    }, 1000),
    groupFuelDashboardByDate(fuel) {
      const groups = new Map()

      for (const item of fuel) {
        const itemDate = moment(item.date)
        const groupDate =
          this.grouping == 'w'
            ? itemDate.startOf('isoWeek').format('YYYY-MM-DD')
            : itemDate.format('YYYY-MM-DD')

        if (!groups.has(groupDate)) {
          groups.set(groupDate, this.createEmptyFuelDashboardGroup(groupDate))
        }

        const group = groups.get(groupDate)
        group.fuelConsumption += this.toChartNumber(item.fuelConsumption)
        group.distanceInKm += this.toChartNumber(item.distanceInKm)
        group.driverAlert += this.toChartNumber(item.driverAlert)
        group.idleDurationInSecond += this.toChartNumber(item.idleDurationInSecond)
        group.tripDurationInSecond += this.toChartNumber(item.tripDurationInSecond)
      }

      return Array.from(groups.values())
        .map((group) => {
          const {
            fuelConsumption,
            distanceInKm,
            driverAlert,
            idleDurationInSecond,
            tripDurationInSecond,
            date,
          } = group

          return {
            date,
            fuelConsumption: fuelConsumption.toFixed(2),
            distanceInKm: distanceInKm.toFixed(2),
            driverAlert,
            idleDurationInSecond,
            tripDurationInSecond,
            fuelEfficiency:
              group.fuelConsumption > 0
                ? (group.distanceInKm / group.fuelConsumption).toFixed(2)
                : 0,
            isWeekGrouping: this.grouping == 'w',
          }
        })
        .sort((first, second) => moment(first.date).valueOf() - moment(second.date).valueOf())
    },
    createEmptyFuelDashboardGroup(date) {
      return {
        date,
        fuelConsumption: 0,
        distanceInKm: 0,
        driverAlert: 0,
        idleDurationInSecond: 0,
        tripDurationInSecond: 0,
        fuelEfficiency: 0,
      }
    },
    mapFuelDashboardDateGrouping(fuel) {
      this.dataChart.line.labels = fuel.map((item) => {
        if (!item.isWeekGrouping) return moment(item.date).format('DD MMM') || '-'
        const startDate = moment(item.date)
        const endDate = moment(item.date).clone().add(6, 'day')
        const finalStartDate = moment.max([startDate, moment(this.period[0])])
        const finalEndDate = moment.min([endDate, moment(this.period[1])])
        return `${finalStartDate.format('DD MMM')} - ${finalEndDate.format('DD MMM')}`
      })
      this.dataChart.line.datasets[0].data = fuel.map((item) =>
        this.toChartNumber(item.fuelConsumption)
      )
      this.dataChart.line.datasets[1].data = fuel.map((item) =>
        this.toChartNumber(item.fuelEfficiency)
      )
      this.dataChart.line.datasets[2].data = fuel.map((item) =>
        this.toChartNumber(item.distanceInKm)
      )
      this.dataChart.line.datasets[3].data = fuel.map((item) =>
        this.toChartNumber(item.driverAlert)
      )
      this.dataChart.line.datasets[4].data = fuel.map((item) =>
        this.toChartNumber((item.idleDurationInSecond / 60).toFixed(0))
      )
      this.dataChart.line.datasets[5].data = fuel.map((item) =>
        this.toChartNumber((item.tripDurationInSecond / 60).toFixed(0))
      )
    },
    mapFuelDashboardVehicleGrouping(fuel) {
      this.dataChart.bar.labels = fuel.map((item) => item.vehicle?.licensePlate || '-')
      this.dataChart.bar.datasetFuel[0].data = fuel.map((item) =>
        this.toChartNumber(item.fuelConsumption)
      )
      this.dataChart.bar.datasetFuel[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetEfficiency[0].data = fuel.map((item) =>
        this.toChartNumber(item.fuelEfficiency)
      )
      this.dataChart.bar.datasetEfficiency[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetTrip[0].data = fuel.map((item) =>
        this.toChartNumber(item.distanceInKm)
      )
      this.dataChart.bar.datasetTrip[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetDriverAlert[0].data = fuel.map((item) =>
        this.toChartNumber(item.driverAlert)
      )
      this.dataChart.bar.datasetDriverAlert[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetIdleDuration[0].data = fuel.map((item) =>
        this.toChartNumber((item.idleDurationInSecond / 60).toFixed(0))
      )
      this.dataChart.bar.datasetIdleDuration[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetTripDuration[0].data = fuel.map((item) =>
        this.toChartNumber((item.tripDurationInSecond / 60).toFixed(0))
      )
      this.dataChart.bar.datasetTripDuration[1].data = this.emptyThresholdData(fuel)
    },
    emptyThresholdData(fuel) {
      return fuel.map(() => 0)
    },
    toChartNumber(value) {
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? 0 : numberValue
    },
    updateFilterLabel() {
      const preiodLabel = `${moment(this.period[0]).format('DD MMM YYYY')} - ${moment(
        this.period[1]
      ).format('DD MMM YYYY')}`
      const vgroupLabel =
        this.vgroupId && this.vgroupId.length > 0
          ? `${this.vgroupId.length} ${this.$t('dashboard.table.vehicleGroup')}`
          : this.$t('global.text.allVehicleGroup')
      const vtypeLabel =
        this.vehicleTypeId && this.vehicleTypeId.length > 0
          ? `${this.vehicleTypeId.length} ${this.$t('dashboard.table.vehicleType')}`
          : this.$t('global.text.allVehicleType')
      this.filterLabel = [preiodLabel, vgroupLabel, vtypeLabel].join(', ')
    },
  },
}
</script>

<style scoped>
.fuel-consumption-trend {
  padding: 16px;
  background: #ffffff;
}

.fuel-consumption-trend__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.fuel-consumption-trend__heading {
  min-width: 0;
}

.fuel-consumption-trend__title {
  margin-bottom: 2px;
  color: #253858;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.fuel-consumption-trend__subtitle {
  display: block;
  color: #253858;
  font-size: 11px;
  line-height: 16px;
}

.fuel-consumption-trend__controls {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 16px;
}

.fuel-consumption-trend__select {
  width: 200px;
}

.fuel-consumption-trend__select ::v-deep .multiselect {
  min-height: 36px;
}

.fuel-consumption-trend__select ::v-deep .multiselect__select {
  height: 36px;
}

.fuel-consumption-trend__select ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 7px 40px 0 12px;
  border: 1px solid #9aa8c0;
  border-radius: 4px;
}

.fuel-consumption-trend__select ::v-deep .multiselect__single {
  margin-bottom: 0;
  color: #344054;
  font-size: 13px;
  line-height: 20px;
}

.fuel-consumption-trend__toggle ::v-deep .btn-group-toggle {
  display: flex;
}

.fuel-consumption-trend__toggle ::v-deep .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 128px;
  min-height: 36px;
  padding: 7px 16px;
  border-color: #0d4491;
  color: #0d4491;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}

.fuel-consumption-trend__toggle ::v-deep .btn i {
  margin-right: 8px;
  font-size: 16px;
}

.fuel-consumption-trend__toggle ::v-deep .btn.active {
  color: #ffffff;
  background-color: #0d4491;
  border-color: #0d4491;
}

.fuel-consumption-trend__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-top: 16px;
}

.fuel-consumption-trend__summary-item {
  display: inline-flex;
  flex: 1 1 160px;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 4px 12px;
  color: #0057b8;
  background: #e8f2ff;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
}

.fuel-consumption-trend__thresholds {
  display: grid;
  grid-template-columns: repeat(6, minmax(128px, 1fr));
  gap: 12px 20px;
  margin-top: 16px;
}

.fuel-consumption-trend__threshold {
  min-width: 0;
}

.fuel-consumption-trend__threshold-heading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 18px;
  color: #667085;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.fuel-consumption-trend__threshold-heading i {
  flex: 0 0 auto;
  margin-right: 4px;
  font-size: 14px;
}

.fuel-consumption-trend__threshold-control {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

.fuel-consumption-trend__threshold-label {
  margin-right: 6px;
  color: #667085;
  font-size: 12px;
  line-height: 28px;
}

.fuel-consumption-trend__threshold-input {
  width: 110px;
  margin: 0;
}

.fuel-consumption-trend__threshold-input ::v-deep.formulate-input,
.fuel-consumption-trend__threshold-input ::v-deep .formulate-input {
  height: 28px;
  padding: 0;
}

.fuel-consumption-trend__threshold-input ::v-deep .formulate-input-wrapper,
.fuel-consumption-trend__threshold-input ::v-deep .formulate-input-element {
  margin: 0;
}

.fuel-consumption-trend__threshold-input ::v-deep .formulate-input-wrapper {
  height: 28px;
  width: 100%;
}

.fuel-consumption-trend__threshold-input ::v-deep .formulate-input-element {
  display: flex;
  align-items: stretch;
  height: 28px;
  width: 100%;
}

.fuel-consumption-trend__threshold-input
  ::v-deep
  .formulate-input-element
  > input:not([type='checkbox']) {
  flex: 1 1 auto;
  width: 5px;
  height: 28px !important;
  min-height: 28px !important;
  padding: 4px 10px !important;
  color: #516078;
  background: #ffffff;
  border: 1px solid #aeb8ca !important;
  border-right: 0 !important;
  border-radius: 4px 0 0 4px !important;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 18px;
}

.fuel-consumption-trend__threshold-input ::v-deep .formulate-input-suffix {
  display: inline-flex;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  width: 59px;
  height: 28px;
  padding: 4px 8px;
  color: #516078;
  background: #e9eef6;
  border: 1px solid #aeb8ca;
  border-radius: 0 4px 4px 0;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.fuel-consumption-trend__chart-viewport {
  max-height: 380px;
  overflow: auto;
  border: 1px solid #edf1f6;
  border-radius: 2px;
  background: #ffffff;
}

.fuel-consumption-trend__chart-row {
  flex-wrap: nowrap;
  width: 100%;
  min-height: 0;
  margin-right: 0;
  margin-left: 0;
  padding-left: 10px;
}

.fuel-consumption-trend__chart-row > [class*='col'] {
  flex: 0 0 16.6667%;
  max-width: 16.6667%;
  min-height: 0;
  padding-right: 0;
  padding-left: 0;
}

.fuel-consumption-trend__chart-row > [class*='col'] + [class*='col'] {
  border-left: 1px solid #edf1f6;
}

.fuel-consumption-trend__chart {
  position: relative;
  overflow: hidden;
  background: #ffffff;
}

.fuel-consumption-trend__chart ::v-deep canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.fuel-consumption-trend__empty {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  color: #667085;
  border: 1px solid #edf1f6;
  border-radius: 2px;
  background: #ffffff;
  font-size: 13px;
}

@media (max-width: 1500px) {
  .fuel-consumption-trend__header {
    flex-direction: column;
  }

  .fuel-consumption-trend__controls {
    flex-wrap: wrap;
    width: 100%;
  }

  .fuel-consumption-trend__thresholds {
    grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  }

  .fuel-consumption-trend__chart-row > [class*='col'] {
    flex-basis: 33.3333%;
    max-width: 33.3333%;
  }
}

@media (max-width: 575.98px) {
  .fuel-consumption-trend {
    padding: 12px;
  }

  .fuel-consumption-trend__select,
  .fuel-consumption-trend__toggle {
    width: 100%;
  }

  .fuel-consumption-trend__toggle ::v-deep .btn-group-toggle,
  .fuel-consumption-trend__toggle ::v-deep .btn {
    width: 100%;
  }

  .fuel-consumption-trend__summary-item {
    flex-basis: 100%;
    white-space: normal;
  }

  .fuel-consumption-trend__thresholds {
    grid-template-columns: 1fr;
  }

  .fuel-consumption-trend__threshold-heading,
  .fuel-consumption-trend__threshold-control {
    justify-content: flex-start;
  }
}

.fuel-consumption-trend__threshold-input ::v-deep input[type='number']::-webkit-outer-spin-button,
.fuel-consumption-trend__threshold-input ::v-deep input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.fuel-consumption-trend__threshold-input ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}
</style>
